/**
 * Global Error Boundary for PDP
 * Prevents page crashes from unhandled errors
 * Waits for DOMContentLoaded to avoid interfering with revenueHunt and other apps
 */

(function() {
  'use strict';

  // Track if we've already initialized
  if (window.pdpErrorBoundaryInitialized) {
    return;
  }

  // Main initialization function
  function initErrorBoundary() {
    // Double check we haven't initialized
    if (window.pdpErrorBoundaryInitialized) {
      return;
    }
    window.pdpErrorBoundaryInitialized = true;

    // Configuration
    const config = {
      maxErrors: 10,
      errorTimeout: 5000, // Reset error count after 5 seconds
      debugMode: false // Set to true to see all errors in console
    };

    let errorCount = 0;
    let errorTimer = null;

    /**
     * Reset error count after timeout
     */
    function resetErrorCount() {
      if (errorTimer) {
        clearTimeout(errorTimer);
      }
      errorTimer = setTimeout(() => {
        errorCount = 0;
      }, config.errorTimeout);
    }

    /**
     * Log error safely
     */
    function logError(error, context) {
      try {
        if (config.debugMode || errorCount < 3) {
          console.error('[PDP Error Boundary]', context, error);
        }
      } catch (e) {
        // Fail silently if console is not available
      }
    }

    /**
     * Check if error should be ignored (from third-party apps like revenueHunt)
     */
    function shouldIgnoreError(event) {
      const filename = event.filename || '';
      const message = event.message || '';
      const stack = (event.error?.stack || '').toString();
      
      // Whitelist: Don't interfere with these third-party apps
      const whitelistPatterns = [
        /revenuehunt/i,
        /revenue-hunt/i,
        /quiz/i,
        /product-recommendation-quiz/i,
        /shopquizkit/i
      ];
      
      // Check if error is from whitelisted source
      const shouldIgnore = whitelistPatterns.some(pattern => 
        pattern.test(filename) || pattern.test(message) || pattern.test(stack)
      );
      
      return shouldIgnore;
    }

    /**
     * Check if error is critical
     */
    function isCriticalError(error) {
      const criticalPatterns = [
        /Cannot read property.*of null/,
        /Cannot read property.*of undefined/,
        /is not a function/,
        /Maximum call stack size exceeded/
      ];

      const errorMessage = error?.message || error?.toString() || '';
      return criticalPatterns.some(pattern => pattern.test(errorMessage));
    }

    /**
     * Global error handler
     */
    window.addEventListener('error', function(event) {
      // IMPORTANT: Don't interfere with third-party apps like revenueHunt
      if (shouldIgnoreError(event)) {
        // Let the error propagate normally for whitelisted apps
        return;
      }

      errorCount++;
      resetErrorCount();

      const error = event.error || event.message;
      const context = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        timestamp: new Date().toISOString()
      };

      logError(error, context);

      // Prevent page crash for non-critical errors
      if (!isCriticalError(error) && errorCount < config.maxErrors) {
        event.preventDefault();
        return false;
      }

      // If we've hit max errors, show user-friendly message
      if (errorCount >= config.maxErrors) {
        showErrorMessage();
        event.preventDefault();
        return false;
      }
    }, true);

    /**
     * Handle unhandled promise rejections
     */
    window.addEventListener('unhandledrejection', function(event) {
      // Check if error is from whitelisted third-party app
      const reasonString = (event.reason?.message || event.reason?.toString() || '').toLowerCase();
      const isWhitelisted = /revenuehunt|revenue-hunt|quiz|product-recommendation-quiz|shopquizkit/.test(reasonString);
      
      if (isWhitelisted) {
        // Don't interfere with third-party apps
        return;
      }

      errorCount++;
      resetErrorCount();

      logError(event.reason, {
        type: 'Unhandled Promise Rejection',
        promise: event.promise,
        timestamp: new Date().toISOString()
      });

      // Prevent unhandled rejection from crashing the page
      if (errorCount < config.maxErrors) {
        event.preventDefault();
        return false;
      }
    });

    /**
     * Show user-friendly error message
     */
    function showErrorMessage() {
      // Only show once
      if (window.pdpErrorMessageShown) {
        return;
      }
      window.pdpErrorMessageShown = true;

      try {
        const message = document.createElement('div');
        message.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 4px;
          padding: 15px 20px;
          z-index: 999999;
          max-width: 300px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.5;
        `;
        message.innerHTML = `
          <strong style="display: block; margin-bottom: 8px;">Notice</strong>
          <p style="margin: 0;">Some features may be temporarily unavailable. Please refresh the page if you experience any issues.</p>
          <button onclick="location.reload()" style="
            margin-top: 10px;
            padding: 6px 12px;
            background: #ffc107;
            border: none;
            border-radius: 3px;
            cursor: pointer;
            font-size: 13px;
          ">Refresh Page</button>
        `;

        document.body.appendChild(message);

        // Auto-hide after 10 seconds
        setTimeout(() => {
          message.style.transition = 'opacity 0.5s';
          message.style.opacity = '0';
          setTimeout(() => message.remove(), 500);
        }, 10000);
      } catch (e) {
        // Fail silently if we can't show the message
      }
    }

    /**
     * Wrap potentially dangerous functions
     */
    function wrapFunction(obj, funcName) {
      if (!obj || typeof obj[funcName] !== 'function') {
        return;
      }

      const originalFunc = obj[funcName];
      obj[funcName] = function(...args) {
        try {
          return originalFunc.apply(this, args);
        } catch (error) {
          logError(error, {
            function: funcName,
            context: 'Wrapped function call'
          });
          return undefined;
        }
      };
    }

    /**
     * Protect critical third-party integrations
     * NOTE: Does NOT wrap revenueHunt or other quiz apps to avoid conflicts
     */
    function protectThirdPartyScripts() {
      // Wait for scripts to load
      setTimeout(() => {
        try {
          // Protect Klaviyo
          if (window._learnq && Array.isArray(window._learnq)) {
            const originalPush = window._learnq.push;
            window._learnq.push = function(...args) {
              try {
                return originalPush.apply(this, args);
              } catch (error) {
                logError(error, { context: 'Klaviyo push' });
              }
            };
          }

          // Protect CartJS
          if (window.CartJS) {
            ['addItem', 'updateItem', 'removeItem'].forEach(method => {
              wrapFunction(window.CartJS, method);
            });
          }

          // Protect Swym Wishlist
          if (window.SwymCallbacks) {
            wrapFunction(window.SwymCallbacks, 'initializeSwymPlugin');
          }

          // Protect Okendo
          if (window.okeReviewsWidgetOnInit) {
            wrapFunction(window, 'okeReviewsWidgetOnInit');
          }

          // IMPORTANT: Do NOT wrap revenueHunt, quiz apps, or product recommendation apps
          // These need to handle their own errors and DOM operations
        } catch (error) {
          logError(error, { context: 'Protecting third-party scripts' });
        }
      }, 2000);
    }

    // Initialize protections
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', protectThirdPartyScripts);
    } else {
      protectThirdPartyScripts();
    }

    // Expose utility for manual error reporting
    window.reportPDPError = function(error, context) {
      logError(error, context || {});
    };
  }

  // Wait for DOMContentLoaded before initializing error boundary
  // This ensures revenueHunt and other third-party apps load first
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // Add additional delay to ensure all third-party scripts are loaded
      setTimeout(initErrorBoundary, 500);
    });
  } else {
    // DOM already loaded, but still add a small delay
    setTimeout(initErrorBoundary, 500);
  }

})();
